/**
 * VRMA プレビューの「描画バックエンド」共通インターフェースと、親・バックエンドで
 * 共有する閾値定数。
 *
 * 親（VrmaPreview）が UI・状態・VrmContext 連携をすべて持ち、バックエンドは
 * 「three.js / Unity WebGL でアセットを読み込んで描画する」ことだけを担う。
 * これによりライト版（three.js）とリッチ版（Unity WebGL）を差し替え可能にする。
 *
 * 定数をこのファイルに置いているのは、親とバックエンドの双方が参照するため。
 * どちらか一方に置くと相互 import になる。
 */

/** シークバーは、この秒数以上のクリップのときだけ表示する。 */
export const SEEK_MIN_DURATION_SEC = 5;

/**
 * これ以下の長さのクリップはほぼ静止ポーズなので、再生/一時停止ボタンを出さず
 * ループ再生のままにする。親は表示の判断に、バックエンドは action.paused の
 * 同期に使うため、両者が同じ値を見る必要がある。
 */
export const PLAY_BTN_MIN_DURATION_SEC = 0.5;

/** バックエンドの読み込み状態。親の overlay 表示の判断に使う。 */
export type VrmaBackendStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface VrmaBackendProps {
  /** 表示する VRM の URL（VrmContext.vrmUrl。サンプルの静的 URL、またはカスタム VRM の objectURL）。 */
  vrmUrl: string;
  /** 再生する VRMA。復号済み blob URL / 静的 URL / ユーザーが選んだ File。 */
  vrmaSource?: string | File;
  /**
   * false のあいだは一切読み込まない（初回 overlay でユーザーの操作を待っている状態）。
   * true になったら vrmUrl を読み、vrmaSource があればそれも適用する。
   */
  armed: boolean;
  /** 再生中かどうか。false で一時停止（ポーズは維持する）。 */
  playing: boolean;
  /** シークバーをドラッグ中。true のあいだ再生時間の進行を止める。 */
  scrubbing: boolean;
  /** 描画してよいか。false のあいだレンダリングを止める（タブ非表示・画面外）。 */
  rendering: boolean;

  /** 読み込み状態が変わったとき。 */
  onStatusChange(status: VrmaBackendStatus): void;
  /** 読み込みが完了したとき。クリップが無い場合は hasClip=false / durationSec=0。 */
  onLoaded(info: { hasVrm: boolean; hasClip: boolean; durationSec: number }): void;
  /** 再生位置の通知（約100ms間隔に間引く）。 */
  onProgress(elapsedSec: number): void;
  /** 復帰可能なエラー。message は overlay にそのまま表示する日本語文。 */
  onError(message: string): void;
  /**
   * このバックエンドがこの環境では使えないと判明したとき（リッチ版のみ使用）。
   * 親はライト版へ自動フォールバックする。
   */
  onUnsupported?(reason: string): void;
}

/** 親から命令的に呼ぶ操作。React の再レンダーを挟みたくないものをここに集める。 */
export interface VrmaBackendHandle {
  /** 再生位置を秒で設定する。一時停止中でもポーズを即時反映する。 */
  seek(seconds: number): void;
  /** カメラを既定の構図（VRM の全身が収まる位置）へ戻す。 */
  resetCamera(): void;
  /** 現在の再生位置（秒）。バックエンドを切り替えるときに引き継ぐため親が読む。 */
  getElapsed(): number;
}

/** VrmaPreview 自身が呼び出し側（Upload.tsx など）へ公開する API。 */
export interface VrmaPreviewHandle {
  /** 視点をリセットする。 */
  resetCamera(): void;
}
